import { Rank } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/** E-M6 — MITRAILLEUR. Le professeur du déplacement TENU.
 *
 *  Il se poste à mi-hauteur et lâche des RAFALES : cinq petits traits rapides
 *  tirés coup sur coup, à peine séparés, tous visés — puis une longue pause, puis
 *  de nouveau. Ce n'est pas une gerbe (toutes les balles d'un coup) mais une
 *  giclée (les balles étalées dans le temps).
 *
 *  ── Ce qu'il enseigne ──
 *  Que tous les tirs visés ne s'esquivent pas de la même façon. Une gerbe
 *  simultanée se déjoue d'UN pas de côté : on quitte la ligne de mire une fois et
 *  tout passe à côté. Une rafale suivie, elle, re-vise à chaque trait — un pas de
 *  côté unique ramène le joueur pile sous le trait suivant. Il faut TENIR un
 *  déplacement pendant toute la salve, ce qui est le premier geste d'esquive
 *  continue du jeu, avant les rideaux des boss.
 *
 *  ── Pourquoi ça ne sature pas l'écran ──
 *  Une salve (5 traits) par cycle de 1,7 s, jamais un flot continu : à ~3 balles
 *  émises par seconde et une traversée d'écran d'un peu plus d'une seconde, il
 *  n'y a qu'une poignée de traits en vol à la fois. Plusieurs Mitrailleurs
 *  restent loin sous le plafond de densité — la rafale est bornée par
 *  construction (voir `Pattern.Burst`).
 *
 *  ── Pourquoi les traits sont RAPIDES ──
 *  820 u/s, presque le double d'une balle ordinaire (450), bien sous le plafond
 *  de 1 500. La vitesse est ce qui les fait lire comme des LASERS et ce qui rend
 *  le déplacement tenu exigeant : trop lents, la salve se contournerait d'une
 *  simple flânerie. La conduite est balistique — la vitesse déclarée est donc la
 *  vitesse finale, sans emballement possible. */
export default defineEnemy({
  id: 'mitrailleur',
  rank: Rank.Standard,
  hp: 140,
  visualRadius: 26,
  score: 240,
  telegraph: 1.1,
  drops: 2,
  // Hover à faible amplitude : il se poste et oscille juste assez pour ne pas
  // être une cible fixe, mais reste un poste de tir, pas un voltigeur.
  motion: { kind: Motion.Hover, speed: 240, amplitude: 55, frequency: 0.7, holdY: 340 },
  emitter: {
    pattern: Pattern.Burst,
    // Cycle : une salve puis la longue pause. Le budget de densité tient à ce
    // que `every` domine `count × burstGap` (5 × 0,07 = 0,35 s de salve contre
    // 1,7 s de cycle).
    every: 1.7,
    // Cinq traits par salve.
    count: 5,
    // 0,07 s entre deux traits : assez serré pour lire une giclée, assez lâche
    // pour laisser le joueur amorcer son déplacement entre le premier et le
    // dernier.
    burstGap: 0.07,
    // Léger balayage réparti sur la salve : la rafale devient un éventail DANS
    // LE TEMPS, ce qui force le déplacement tenu au lieu d'un pas figé.
    spreadDeg: 12,
    // 820 u/s : le trait rapide qui se lit comme un laser. Sous le plafond de
    // 1 500 même majoré du palier Vétéran (×1,12 = 918).
    speed: 820,
    firstDelay: 1.3,
    family: Fam.EnemyPink,
  },
});
